import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseService } from 'cre-ng';

import {
  BonusPayableMonth,
  BonusStatByMonth,
  BonusStats,
  BonusStatusByMonth,
  DisplayableType,
  IDayDetail,
  IDriverStats,
  OptInStatus,
} from '@app/core/model/qualifications.model';
import { IBonusQualificationService } from '@app/core/services/interface/IBonusQualificationService';

@Injectable()
export class BonusQualificationService extends BaseService implements IBonusQualificationService {
  constructor(protected httpClient: HttpClient) {
    super({ serviceApiDomain: window.__env.ngSettings.serviceApiDomain }, httpClient);
  }

  getBonusPayableMonths(driverCode: string): Promise<BonusPayableMonth[]> {
    const httpHeaders = this.getHttpHeaders();
    const requestOptions = { headers: httpHeaders };
    return this.httpClient
      .get<BonusPayableMonth[]>(
        this.serviceUrl + `/qualifications/bonusdisplaypayablemonths/${driverCode}`,
        requestOptions
      )
      .toPromise();
  }

  getBonusStatus(
    driverCode: string,
    displayableType: DisplayableType
  ): Promise<BonusStatusByMonth[]> {
    const httpHeaders = this.getHttpHeaders();
    const requestOptions = { headers: httpHeaders };
    return this.httpClient
      .get<BonusStatusByMonth[]>(
        this.serviceUrl + `/qualifications/bonusStatus/${driverCode}`,
        requestOptions
      )
      .toPromise();
  }

  getBonusStatusByMonth(driverCode: string, month: Date): Promise<BonusStatByMonth> {
    const httpHeaders = this.getHttpHeaders();
    const requestOptions = { headers: httpHeaders };
    return this.httpClient
      .get<BonusStatByMonth>(
        this.serviceUrl + `/qualifications/bonusstatus/${driverCode}/${month}`,
        requestOptions
      )
      .toPromise();
  }

  getCommitmentDetails(
    driverId: string,
    leadSubject: string,
    startdaysAgo: number,
    endDaysAgo: number
  ): Promise<IDayDetail[]> {
    const httpHeaders = this.getHttpHeaders();
    const requestOptions = { headers: httpHeaders };
    return this.httpClient
      .get<IDayDetail[]>(
        this.serviceUrl +
          `/qualifications/commitments/daysago/${driverId}/${encodeURIComponent(
            leadSubject
          )}/${startdaysAgo}/${endDaysAgo}`,
        requestOptions
      )
      .toPromise();
  }

  getCommitmentDetailsByLeadId(driverId: string, leadId: number): Promise<IDayDetail[]> {
    const httpHeaders = this.getHttpHeaders();
    const requestOptions = { headers: httpHeaders };
    const timeOfTheCall = new Date().getTime();

    return this.httpClient
      .get<IDayDetail[]>(
        this.serviceUrl +
          `/qualifications/commitments/byleadId/${driverId}/${leadId}?${timeOfTheCall}`,
        requestOptions
      )
      .toPromise();
  }

  getDisplayableStats(driverCode: string, qualifyPeriod: string): Promise<BonusStats> {
    const httpHeaders = this.getHttpHeaders();
    const requestOptions = { headers: httpHeaders };
    return this.httpClient
      .get<BonusStats>(
        this.serviceUrl + `/qualifications/${driverCode}/${qualifyPeriod}`,
        requestOptions
      )
      .toPromise();
  }

  getDriverStats(driverId: string): Promise<IDriverStats[]> {
    const httpHeaders = this.getHttpHeaders();
    const requestOptions = { headers: httpHeaders };
    const timeOfTheCall = new Date().getTime();
    return this.httpClient
      .get<IDriverStats[]>(
        this.serviceUrl + `/qualifications/${driverId}?${timeOfTheCall}`,
        requestOptions
      )
      .toPromise();
  }

  getDriverStatsByQualifyPeriod(
    driverId: string,
    qualifyPeriod: Date,
    includeBonusData: boolean
  ): Promise<IDriverStats> {
    const httpHeaders = this.getHttpHeaders();
    const requestOptions = { headers: httpHeaders };
    const timeOfTheCall = new Date().getTime();
    return this.httpClient
      .get<IDriverStats>(
        this.serviceUrl +
          `/qualifications/${driverId}/${qualifyPeriod}?${timeOfTheCall}&includeBonusData=${includeBonusData}`,
        requestOptions
      )
      .toPromise();
  }

  getOptInDetails(driverCode: string): Promise<OptInStatus> {
    const httpHeaders = this.getHttpHeaders();
    const requestOptions = { headers: httpHeaders };
    return this.httpClient
      .get<OptInStatus>(this.serviceUrl + `/qualifications/optin/${driverCode}`, requestOptions)
      .toPromise();
  }

  optInDriver(driverCode: string, startDate: string): Promise<string> {
    const httpHeaders = this.getHttpHeaders();
    const requestOptions = { headers: httpHeaders };
    return this.httpClient
      .post<string>(
        this.serviceUrl + `/qualifications/optin/${driverCode}/${startDate}`,
        requestOptions
      )
      .toPromise();
  }

  protected getEndPoint(): string {
    return '/safeandontime/api/v1';
  }
}
